import { tLeadMessageEvent } from 'src/providers/SocketsProvider/Sockets.types';

export type tShouldNotifyLeadMessageParams = {
  event: tLeadMessageEvent;
  currentWorkspaceId: string | undefined;
  currentUserUuid: string | undefined;
};

/**
 * Decides whether an inbound `lead.message.created` interrupts the viewer with the
 * message toast and its sound (BUG-894).
 *
 * The event reaches everyone who may SEE the lead, which since BUG-848 is every
 * owner/admin and every member of a public funnel, and seeing a chat is not the same
 * as being interrupted by it. Nothing notifies until the active workspace is known,
 * and never for another workspace. An assigned lead notifies only its assignee; an
 * unassigned lead is the shared pool and notifies every recipient, as before BUG-848.
 * A payload without `assigned_user_uuid` comes from a producer that predates the
 * field and keeps notifying every recipient, so this ships safely in either order
 * with leads-service. The board, the other consumer of this event, stays ungated.
 */
export function shouldNotifyLeadMessage(
  params: tShouldNotifyLeadMessageParams,
): boolean {
  const { event, currentWorkspaceId, currentUserUuid } = params;

  // Until the active workspace is known (hydration), an event from another
  // workspace is indistinguishable from one of ours: notify nothing.
  if (!currentWorkspaceId || event.workspace_id !== currentWorkspaceId) {
    return false;
  }

  const assigneeUuid = event.assigned_user_uuid;
  if (assigneeUuid == null) return true;

  // Viewer not known yet: an extra alert beats missing one about their own lead.
  if (!currentUserUuid) return true;

  return assigneeUuid === currentUserUuid;
}
